<template>
  <header class="site-header">
    <div :class="['logo-section', { collapsed: props.isMenuCollapsed }]">
      <div class="menu-btn" @click="toggleMenu">
        <SvgIcon name="svg/menu_header.svg" class="menu-icon-svg" />
      </div>
      <RouterLink to="/" class="site-logo">
        <img :src="getWebpUrl('imgs/sideMenu/logo.webp')" alt="logo" />
      </RouterLink>
      <div class="gap" />
      <RouterLink to="/" class="site-logo-pt">
        <img :src="getWebpUrl('imgs/sideMenu/pt.webp')" alt="logo" />
      </RouterLink>
    </div>
    <div class="header-content">
      <div class="user-actions">
        <template v-if="!isLoggedIn">
          <button class="login-btn" @click="handleLogin">{{ $t('登录') }}</button>
          <button class="register-btn" @click="handleRegister">{{ $t('注册') }}</button>
          <div
            class="lang"
            role="button"
            tabindex="0"
            @click="handleSwitchLang"
            @keydown.enter.prevent="handleSwitchLang"
            @keydown.space.prevent="handleSwitchLang">
            <img :src="currentLangIcon" :alt="currentLangCode" class="lang-icon" />
            <span class="lang-divider" aria-hidden="true" />
            <span class="lang-code">{{ currentLangShort }}</span>
          </div>
        </template>
        <template v-else>
          <div class="user-info">
            <div class="balance-card">
              <SvgIcon name="svg/icon_coin.svg" class="balance-coin" />
              <div class="balance-text">
                <span class="balance-label">{{ $t('Balance') }}:</span>
                <span class="balance-value">{{ formattedBalance }}</span>
              </div>
              <button class="refresh-btn" type="button" @click="handleRefreshBalance">
                <SvgIcon
                  name="svg/icon_refresh.svg"
                  :class="['refresh-icon', { spinning: isRefreshSpinning }]" />
              </button>
            </div>

            <button class="deposit-btn" type="button" @click="handleDeposit">
              {{ $t('存款') }}
            </button>

            <div
              class="profile-card"
              @mouseenter="handleShowPopover(HoverType.USER, $event)"
              @mouseleave="handleMouseLeaveAvatar(HoverType.USER, $event)"
              role="button"
              tabindex="0">
              <img
                v-if="userInfo?.avatar"
                :style="userInfo?.avatar ? {} : { visibility: 'hidden' }"
                :src="userInfo?.avatar"
                :alt="userInfo?.username"
                class="profile-avatar" />
              <SvgIcon v-else name="svg/icon_user.svg" size="35px" />
              <div class="profile-meta">
                <span class="profile-name">{{ displayUsername }}</span>
                <span class="vip-tag" v-if="vipIcon && vipLevel">
                  <img :src="vipIcon" alt="vip" class="vip-icon" />
                  <span>{{ $t(vipNameKey) }}</span>
                </span>
              </div>
              <span
                :class="[
                  'profile-arrow',
                  { 'profile-arrow--expanded': hoveredKey === HoverType.USER }
                ]">
                <SvgIcon name="svg/down_arrow.svg" class="profile-arrow-icon" />
              </span>
            </div>

            <div class="notification-wrap">
              <RouterLink class="notification-btn" to="/notice">
                <SvgIcon name="svg/icon_notification_header.svg" class="notification-icon" />
                <span v-if="hasUnread" class="notification-dot" />
              </RouterLink>
            </div>

            <div
              class="lang"
              role="button"
              tabindex="0"
              @click="handleSwitchLang"
              @keydown.enter.prevent="handleSwitchLang"
              @keydown.space.prevent="handleSwitchLang">
              <img :src="currentLangIcon" :alt="currentLangCode" class="lang-icon" />
              <span class="lang-divider" aria-hidden="true" />
              <span class="lang-code">{{ currentLangShort }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>
    <AnchoredPopup
      :gap="13"
      :viewportMargin="10"
      :deviation="0"
      :showArrow="false"
      ref="popoverRef"
      @after-hide="handleHidePopover"
      :rootClass="styles.headerPopover"
      placement="bottom">
      <AccountMenu
        :showLastItem="true"
        v-if="hoveredKey === HoverType.USER"
        @select="handleAccountMenuSelect" />
    </AnchoredPopup>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { showLogin, showRegister } from '@/utils/authDialog'
import type { UserData } from '@/utils/authDialog'
import SvgIcon from '@/components/SvgIcon.vue'
import { useToken } from '@/composables/useToken'
import { useBalance } from '@/composables/useBalance'
import useUserInfo from '@/composables/useUserInfo'
import AnchoredPopup from '@/components/base/popover/AnchoredPopover.vue'
import AccountMenu from '../AccountMenu.vue'
import { HoverType } from '@/types/home'
import { useStableEnter } from '@/composables/useStableEnter'
import { useNoticeCenter } from '@/composables/useNoticeCenter'
import { getWebpUrl } from '@/utils/webpAssets'
import { formatNum } from '@/utils/numeral'
import { useRouter } from 'vue-router'
import { showLanguageSwitchPopup } from '@/components/LanguageSwitchPopup'
import { useSupportLanguages } from '@/composables/useSupportLanguages'
import { useSiteDict } from '@/composables/useSiteDict.ts'
import { getVipLevelImage, getVipLevelNameKey } from '@/config/vipLevel'

// Props
interface Props {
  isMenuCollapsed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isMenuCollapsed: false
})

// Emits
const emit = defineEmits<{
  toggleMenu: []
}>()
const stableEnter = useStableEnter({ delay: 150 })

const hoveredKey = ref<HoverType>()
const popoverRef = ref<InstanceType<typeof AnchoredPopup> | null>(null)
// 从composables获取登录状态/用户信息/未读消息
const { isLoggedIn, setToken } = useToken()
const { userInfo, readVipMember } = useUserInfo()
const { balance, refreshBalance } = useBalance()
const { siteCurrency } = useSiteDict()
const { totalUnreadCount, getTotalUnreadCount } = useNoticeCenter()
const { initLanguages, currentLang, currentLangCode } = useSupportLanguages()
const router = useRouter()

const currentLangShort = computed(() => {
  const [shortCode] = currentLangCode.value.split('-')
  return (shortCode || 'en').toUpperCase()
})

const currentLangIcon = computed(() => {
  return currentLang.value?.img || getWebpUrl('imgs/sideMenu/icon-language.webp')
})

const formattedBalance = computed(
  () => `${siteCurrency.value}${formatNum({ number: balance.value })}`
)
const hasUnread = computed(() => Number(totalUnreadCount.value) > 0)

const displayUsername = computed(() => userInfo.value?.username || '--')

const vipLevel = computed(() => readVipMember.value?.vipLevel)
const vipNameKey = computed(() => getVipLevelNameKey(vipLevel.value))
const vipIcon = computed(() => {
  const apiIcon = getVipLevelImage(vipLevel.value)
  return apiIcon && getWebpUrl(apiIcon)
})
const isRefreshSpinning = ref(false)

// 处理菜单切换
const toggleMenu = () => {
  emit('toggleMenu')
}

// 处理登录
const handleLogin = () => {
  showLogin((userData: UserData) => {
    console.log('用户登录成功:', userData)
  })
}

// 处理注册
const handleRegister = () => {
  showRegister((userData: UserData) => {
    if (userData.accessToken) {
      setToken(userData.accessToken)
    }
    console.log('用户注册成功:', userData)
  })
}

const handleSwitchLang = showLanguageSwitchPopup

const handleDeposit = () => {
  router.push('/deposit')
}

const handleRefreshBalance = () => {
  isRefreshSpinning.value = true
  refreshBalance()
  setTimeout(() => {
    isRefreshSpinning.value = false
  }, 700)
}

// 处理hover事件
const handleShowPopover = (key: HoverType, event: MouseEvent) => {
  const el = event.currentTarget as HTMLElement | null
  stableEnter.reTarget(key, el, () => {
    hoveredKey.value = key
    popoverRef.value?.show(event)
  })
}

// 处理鼠标移出事件，为了debounce效果
const handleMouseLeaveAvatar = (key: HoverType, event: MouseEvent) => {
  stableEnter.cancel(key)
}

const handleHidePopover = () => {
  hoveredKey.value = undefined
}

const handleAccountMenuSelect = () => {
  popoverRef.value?.hideNow()
}

// 登录后刷新未读消息
watch(isLoggedIn, (v) => {
  if (v) {
    getTotalUnreadCount()
  }
})

onMounted(() => {
  initLanguages()
})
</script>

<style scoped lang="scss">
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  height: 70px;
  display: flex;
  background: linear-gradient(180deg, #11100d 0.01%, #282622 97.12%);

  .header-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 50px;
  }

  .logo-section {
    flex-shrink: 0;
    display: flex;
    position: relative;

    .menu-btn,
    .gap,
    .site-logo,
    .site-logo-pt {
      align-self: center;
    }

    .menu-btn {
      border: 0.5px solid #ffffff33;
      box-sizing: border-box;
      flex: 0 0 42px;
      width: 42px;
      height: 42px;
      border-radius: 8px;
      margin-left: 14px;
      margin-right: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;
      font-size: 20px;
      .menu-icon-svg {
        font-size: 30px;
      }
    }

    .gap {
      width: 0.4px;
      height: 40px;
      background-color: #504530;
      margin-left: 14px;
      margin-right: 14px;
      display: block;
    }

    .site-logo,
    .site-logo-pt {
      margin: 0 auto;
      display: flex;
      align-items: stretch;

      img {
        flex: 1;
      }
    }

    .site-logo {
      width: 85px;
      height: 37px;
      margin-left: 5px;
    }

    .site-logo-pt {
      // width: 140px;
      height: 20px;
    }
  }

  .user-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-shrink: 0;

    .login-btn,
    .register-btn,
    .lang {
      appearance: none;
      min-width: 110px;
      height: 45px;
      padding: 0 15px;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      font-weight: 700;
      font-size: 15px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        opacity: 0.8;
      }
    }

    .login-btn {
      border: 0.4px solid transparent;
      background:
        linear-gradient(90deg, #d6a443 0%, #c07526 72%, #a15816 100%) padding-box,
        linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%) border-box;
      color: #fff;

      &:hover {
        color: #fff;
      }
    }

    .register-btn {
      border: 0.4px solid #735237;
      background: transparent;
      color: #f5b75c;
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .balance-card {
      min-width: 164px;
      height: 45px;
      border-radius: 8px;
      background: #3d3d3d;
      display: flex;
      align-items: center;
      padding: 0 10px;
      box-sizing: border-box;

      .balance-coin {
        font-size: 25px;
        flex-shrink: 0;
      }

      .balance-text {
        margin: 0 8px;
        display: flex;
        flex-direction: column;
        line-height: 1.1;
      }

      .balance-label {
        font-size: 12px;
        color: #fff;
      }

      .balance-value {
        margin-top: 2px;
        font-size: 14px;
        font-weight: 700;
        color: #ffee30;
      }

      .refresh-btn {
        margin-left: auto;
        width: 20px;
        height: 20px;
        border: 0;
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        .refresh-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transform-origin: center;
          font-size: 20px;
          &.spinning {
            animation: refresh-spin 0.7s linear;
          }
        }
      }
    }

    .deposit-btn {
      width: 110px;
      height: 45px;
      border: 0;
      border-radius: 8px;
      background: linear-gradient(90deg, #d6a443 0%, #c07526 72%, #a15816 100%);
      color: #fff;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
    }

    .profile-card {
      min-width: 169px;
      height: 45px;
      border: 0.4px solid #555;
      border-radius: 8px;
      display: flex;
      align-items: center;
      padding: 0 10px;
      box-sizing: border-box;
      cursor: pointer;

      .profile-avatar {
        width: 35px;
        height: 35px;
        border-radius: 50%;
        object-fit: cover;
        flex-shrink: 0;
      }

      .profile-meta {
        margin-left: 8px;
        display: flex;
        flex-direction: column;
      }

      .profile-name {
        max-width: 200px;
        font-size: 14px;
        line-height: 18px;
        font-weight: 700;
        color: #ad884c;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .vip-tag {
        height: 18px;
        width: 61px;
        border-radius: 50px;
        background: #4d3623;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        font-size: 10px;
        color: #fff;
      }

      .vip-icon {
        width: 13px;
        height: 14px;
        object-fit: cover;
      }

      .profile-arrow {
        margin-left: auto;
        width: 24px;
        height: 24px;
        border-radius: 5px;
        background: #37342d;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 10px;

        .profile-arrow-icon {
          transform: rotate(-90deg);
          transition: transform 0.2s ease;
        }

        &:hover .profile-arrow-icon,
        &--expanded .profile-arrow-icon {
          transform: rotate(0);
        }
      }
    }

    .notification-wrap {
      display: inline-flex;
    }

    .notification-btn {
      position: relative;
      width: 45px;
      height: 45px;
      border: 0.5px solid #555;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      .notification-icon {
        width: 25px;
        height: 25px;
      }

      .notification-dot {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: #f63838;
        animation: attention-dot-blink 1.2s ease-in-out infinite;
      }
    }

    .lang {
      min-width: 96px;
      border: 0.4px solid #555555;
      gap: 12px;
      user-select: none;

      .lang-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .lang-divider {
        width: 0.4px;
        height: 20px;
        background: #ffffff66;
        flex-shrink: 0;
      }

      .lang-code {
        font-family: Inter, sans-serif;
        font-size: 14px;
        font-weight: 700;
        line-height: 1;
        color: #fff;
      }
    }
  }
}

@keyframes refresh-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
<style lang="css" module="styles">
.headerPopover {
  --anchor-po-border-color: transparent;
  --anchor-po-whole-border: none;
  --anchor-po-box-shadow: box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.4);

  --anchor-po-box-radius: 8px;
  --anchor-po-padding: 0;
  backdrop-filter: blur(40px);
  border-radius: 8px;
}
</style>
